import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Box from '@mui/material/Box';

import AddressField from './AddressField';
import BooleanField from './BooleanField';
import CurrencyField from './CurrencyField';
import CustomInputLabel from './CustomInputLabel';
import CustomNumberField from './CustomNumberField';
import CustomSelect from './CustomSelect';
import CustomTextField from './CustomTextField';
import DateField from './DateField';
import DateRangePicker from './DateRangeFilter';
import FindCaseInput from '../cases/FindCaseInput';
import FindPatientForm from '../patients/FindPatientForm';
import FindPayerInput from '../payers/FindPayerInput';
import FindReferrerInput from '../referrers/FindReferrerInput';
import HtmlEditor from './HtmlEditor';
import PhoneInput from './PhoneInput';
import SelectLocationInput from '../locations/SelectLocationInput';
import SelectMarketInput from '../markets/SelectMarketInput';
import SelectProviderInput from '../providers/SelectProviderInput';
import SelectRegionInput from '../regions/SelectRegionInput';
import SelectTeamInput from '../teams/SelectTeamInput';
import SimpleDateField from './SimpleDateField';
import PayerPlanSelect from '../payer-plans/PayerPlanSelect';

const FormInput = props => {
  const [blurred, setBlurred] = useState(false);
  const childProps = {
    ...props,
    error: !(props?.validate ? props.validate(props.value) : true) && blurred,
    onBlur: () => setBlurred(true),
  };
  if (props.hidden) return <></>;
  switch (props.type) {
    case 'text':
      return <CustomTextField {...childProps} />;
    case 'textarea':
      return (
        <CustomTextField
          inputType='textarea'
          rows={5}
          multiline
          {...childProps}
        />
      );
    case 'number':
      return (
        <CustomNumberField
          type='number'
          {...childProps}
        />
      );
    case 'currency': 
      return <CurrencyField {...childProps} />;
    case 'options':
      return <CustomSelect {...childProps} />;
    case 'select':
      return <CustomSelect {...childProps} />;
    case 'boolean':
      return <BooleanField {...childProps} />;
    case 'email':
      return <CustomTextField type='email' {...childProps} />;
    case 'password':
      return <CustomTextField type='password' {...childProps} />;
    case 'phone':
      return <PhoneInput {...childProps} />;
    case 'html':
      return <HtmlEditor {...childProps} />;
    case 'date':
      return <DateField {...childProps} />;
    case 'simpleDate':
      return <SimpleDateField {...childProps} />;
    case 'daterange':
      return (
        <DateRangePicker
          startDate={
            get(props, 'value.start')
              ? moment(props.value.start)
              : null
          }
          endDate={
            get(props, 'value.end')
              ? moment(props.value.end)
              : null
          }
          value={props.value}
          onChange={props.onChange}
        />
      );
    case 'selectPatient':
      return (
        <Box style={{paddingTop: '0.5em'}}>
          <CustomInputLabel required={props?.required}>
            {props?.label || 'Selected Patient'}
          </CustomInputLabel>
          <FindPatientForm 
            allPatients={isBoolean(childProps.allPatients) ? childProps.allPatients : true} 
            {...childProps} 
            align='flex-start'
            color={props?.color || 'primary'}
            size='small'
            onSubmit={childProps.onChange} 
          />
        </Box>
      );
    case 'selectProvider':
      return (
        <SelectProviderInput {...childProps} />
      );
    case 'selectPayer':
      return (
        <FindPayerInput 
          {...childProps} 
          defaultPayerId={childProps?.defaultValue}
          onSubmit={childProps.onChange} 
        />
      );
    case 'selectLocation':
      return (
        <SelectLocationInput
          {...childProps}
          onChange={childProps.onChange}
        />
      );
    case 'selectCase':
      // requires props.patientId!
      return <FindCaseInput {...childProps} onSubmit={childProps.onChange} />;
    case 'selectPayerPlan':
      return <PayerPlanSelect {...childProps} onSubmit={childProps.onChange} />;
    case 'selectReferrer':
      return <FindReferrerInput {...childProps} onSubmit={childProps.onChange} />;
    case 'selectRegion':
      return <SelectRegionInput {...childProps} onChange={childProps.onChange} />;
    case 'selectMarket':
      return <SelectMarketInput {...childProps} onChange={childProps.onChange} />;
    case 'selectTeam':
      return <SelectTeamInput {...childProps} onChange={childProps.onChange} />;
    case 'address':
      return (
        <AddressField 
          {...childProps} 
          onChange={address => childProps.onChange({target: {value: address}}, address)}
        />
      );
    default:
      return <CustomTextField {...childProps} />;
  }
};

FormInput.propTypes = {
  type: PropTypes.string.isRequired,
};

export default FormInput;